/**
 * Mobile Revival - Shopify 2.0 Theme JavaScript
 * Vanilla JS, no dependencies. ES6+.
 */

(function () {
  'use strict';

  /* ==========================================================================
     Utility Helpers
     ========================================================================== */

  function debounce(fn, delay) {
    let timer = null;
    return function () {
      const context = this;
      const args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  function formatMoney(cents) {
    const dollars = (cents / 100).toFixed(2);
    return '$' + dollars;
  }

  function escapeHtml(str) {
    if (typeof str !== 'string') return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function trapFocus(container) {
    const focusable = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    container.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });
  }

  /* Body scroll lock — counter pattern so multiple drawers/modals coexist */
  var _bodyLockCount = 0;
  function lockBodyScroll() {
    if (_bodyLockCount === 0) document.body.style.overflow = 'hidden';
    _bodyLockCount++;
  }
  function unlockBodyScroll() {
    _bodyLockCount = Math.max(0, _bodyLockCount - 1);
    if (_bodyLockCount === 0) document.body.style.overflow = '';
  }

  /* ==========================================================================
     1. Mobile Menu Toggle
     ========================================================================== */

  function initMobileMenu() {
    // Mobile menu open/close and accordion handling lives in the inline
    // <script> at the bottom of header.liquid (section-scoped, uses data
    // attributes, manages aria state).  Adding duplicate handlers here
    // caused a race condition where the toggle opened then immediately
    // closed the menu.  This stub is kept so safeInit('MobileMenu', …)
    // does not throw.

    var menu = document.querySelector('.mobile-menu');
    if (!menu) return;

    // Escape key fallback (inline script does not add one)
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-active')) {
        menu.classList.remove('is-active');
        var overlay = document.querySelector('.mobile-menu__overlay');
        if (overlay) overlay.classList.remove('is-active');
        document.body.style.overflow = '';
      }
    });
  }

  /* ==========================================================================
     2. Mega Menu (Desktop)
     ========================================================================== */

  function initMegaMenu() {
    const navItems = document.querySelectorAll('.nav__item');

    navItems.forEach(function (item) {
      const dropdown = item.querySelector('.mega-menu') || item.querySelector('.nav__dropdown');
      if (!dropdown) return;

      // Keyboard accessibility: toggle on Enter/Space
      const link = item.querySelector('.nav__link');
      if (link && dropdown) {
        link.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const isOpen = item.classList.contains('is-active');
            // Close all
            navItems.forEach(function (ni) {
              ni.classList.remove('is-active');
            });
            if (!isOpen) {
              item.classList.add('is-active');
            }
          }
          if (e.key === 'Escape') {
            item.classList.remove('is-active');
            link.focus();
          }
        });
      }
    });
  }

  /* ==========================================================================
     3. Announcement Bar Dismiss
     ========================================================================== */

  function initAnnouncementBar() {
    var bar = document.querySelector('.announcement-bar');
    var dismissBtn = document.querySelector('.announcement-bar__dismiss');

    if (!bar) return;

    if (sessionStorage.getItem('mr_announcement_dismissed') === 'true') {
      bar.style.display = 'none';
      return;
    }

    if (dismissBtn) {
      dismissBtn.addEventListener('click', function () {
        bar.style.transition = 'max-height 0.3s ease, opacity 0.3s ease, padding 0.3s ease';
        bar.style.maxHeight = bar.scrollHeight + 'px';
        requestAnimationFrame(function () {
          bar.style.maxHeight = '0';
          bar.style.opacity = '0';
          bar.style.paddingTop = '0';
          bar.style.paddingBottom = '0';
          bar.style.overflow = 'hidden';
        });
        sessionStorage.setItem('mr_announcement_dismissed', 'true');
        setTimeout(function () {
          bar.style.display = 'none';
        }, 320);
      });
    }
  }

  /* ==========================================================================
     4. Predictive Search
     ========================================================================== */

  function initPredictiveSearch() {
    var input = document.querySelector('.header__search-input');
    var resultsContainer = document.querySelector('.predictive-search');
    var searchForm = document.querySelector('.header__search-form');
    var abortController = null;

    if (!input || !resultsContainer) return;

    function showResults() {
      resultsContainer.classList.add('is-active');
    }

    function hideResults() {
      resultsContainer.classList.remove('is-active');
    }

    function showLoading() {
      resultsContainer.innerHTML = '<div class="predictive-search__loading">Searching...</div>';
      showResults();
    }

    function renderResults(products) {
      if (!products || products.length === 0) {
        resultsContainer.innerHTML = '<div class="predictive-search__empty">No results found</div>';
        showResults();
        return;
      }

      var html = '<ul class="predictive-search__list">';
      products.forEach(function (product) {
        var image = product.image || product.featured_image || '';
        var imageUrl = '';
        if (typeof image === 'string') {
          imageUrl = image;
        } else if (image && image.url) {
          imageUrl = image.url;
        }
        var price = product.price ? formatMoney(parseInt(product.price, 10)) : '';
        var title = escapeHtml(product.title || '');
        var url = escapeHtml(product.url || '/products/' + (product.handle || ''));
        var safeImageUrl = escapeHtml(imageUrl);

        html += '<li class="predictive-search__item">';
        html += '<a href="' + url + '" class="predictive-search__link">';
        if (imageUrl) {
          html += '<img class="predictive-search__image" src="' + safeImageUrl + '" alt="' + title + '" width="60" height="60" loading="lazy">';
        }
        html += '<div class="predictive-search__info">';
        html += '<span class="predictive-search__title">' + title + '</span>';
        if (price) {
          html += '<span class="predictive-search__price">' + price + '</span>';
        }
        html += '</div>';
        html += '</a>';
        html += '</li>';
      });
      html += '</ul>';

      resultsContainer.innerHTML = html;
      showResults();
    }

    function fetchResults(query) {
      if (abortController) {
        abortController.abort();
      }
      abortController = new AbortController();

      var encodedQuery = encodeURIComponent(query);
      var url = '/search/suggest.json?q=' + encodedQuery + '&resources[type]=product&resources[limit]=6';

      showLoading();

      fetch(url, { signal: abortController.signal })
        .then(function (response) {
          return response.json();
        })
        .then(function (data) {
          var products = [];
          if (data.resources && data.resources.results && data.resources.results.products) {
            products = data.resources.results.products;
          }
          renderResults(products);
        })
        .catch(function (err) {
          if (err.name !== 'AbortError') {
            resultsContainer.innerHTML = '<div class="predictive-search__error">Something went wrong</div>';
            showResults();
          }
        });
    }

    var debouncedFetch = debounce(function () {
      var query = input.value.trim();
      if (query.length < 2) {
        hideResults();
        resultsContainer.innerHTML = '';
        return;
      }
      fetchResults(query);
    }, 300);

    input.addEventListener('input', debouncedFetch);

    input.addEventListener('focus', function () {
      if (input.value.trim().length >= 2 && resultsContainer.innerHTML.trim() !== '') {
        showResults();
      }
    });

    // Close on click outside
    document.addEventListener('click', function (e) {
      if (searchForm && !searchForm.contains(e.target) && !resultsContainer.contains(e.target)) {
        hideResults();
      }
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        hideResults();
        input.blur();
      }
    });
  }

  /* ==========================================================================
     5. AJAX Cart (Add to Cart)
     ========================================================================== */

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    var action = form.getAttribute('action') || '';
    return action === '/cart/add' || action.indexOf('/cart/add') !== -1;
  }

  function initAjaxCart() {
    // Intercept all add-to-cart form submissions in capture phase
    // (fires before Shopify's own form handlers)
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!isCartAddForm(form)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      submitAddToCart(form);
    }, true);

    // Also intercept clicks on submit buttons inside cart forms in capture phase
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('button[type="submit"], input[type="submit"]');
      if (!btn) return;
      var form = btn.closest('form');
      if (!isCartAddForm(form)) return;

      // Quick-add button path: use data-variant-id if available for a faster JSON request
      var quickAddBtn = btn.closest('.product-card__quick-add');
      if (quickAddBtn) {
        e.preventDefault();
        e.stopImmediatePropagation();

        var variantId = quickAddBtn.getAttribute('data-variant-id');
        // Fallback: read from the form's hidden input
        if (!variantId) {
          var hiddenInput = form.querySelector('input[name="id"]');
          if (hiddenInput) variantId = hiddenInput.value;
        }
        if (!variantId) return;

        quickAddBtn.classList.add('is-loading');
        quickAddBtn.disabled = true;

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }] })
        })
          .then(function (response) {
            if (!response.ok) {
              return response.json().then(function (err) {
                throw new Error(err.description || 'Could not add item to cart');
              });
            }
            return response.json();
          })
          .then(function () {
            updateCartCount();
            window.location.href = '/cart';
          })
          .catch(function (err) {
            showCartError(err.message || 'Item is out of stock');
            quickAddBtn.classList.remove('is-loading');
            quickAddBtn.disabled = false;
          });
        return;
      }

      // Regular add-to-cart button
      e.preventDefault();
      e.stopImmediatePropagation();
      submitAddToCart(form);
    }, true);
  }

  function submitAddToCart(form) {
    var submitBtn = form.querySelector('[type="submit"]');
    var originalText = '';
    if (submitBtn) {
      originalText = submitBtn.textContent;
      submitBtn.textContent = 'Adding...';
      submitBtn.disabled = true;
    }

    var variantInput = form.querySelector('input[name="id"]');
    var quantityInput = form.querySelector('input[name="quantity"]');
    var variantId = variantInput ? variantInput.value : '';
    var quantity = quantityInput ? parseInt(quantityInput.value, 10) || 1 : 1;

    if (!variantId) {
      showCartError('Please select a product variant');
      if (submitBtn) {
        submitBtn.textContent = originalText;
        submitBtn.disabled = false;
      }
      return;
    }

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: quantity }] })
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || 'Could not add item to cart');
          });
        }
        return response.json();
      })
      .then(function () {
        if (submitBtn) {
          submitBtn.textContent = 'Added! Redirecting...';
        }
        window.location.href = '/cart';
      })
      .catch(function (err) {
        showCartError(err.message || 'Item is out of stock');
        if (submitBtn) {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }
      });
  }

  function showCartError(message) {
    var existing = document.querySelector('.cart-error-toast');
    if (existing) existing.remove();

    var toast = document.createElement('div');
    toast.className = 'cart-error-toast';
    toast.textContent = message;
    toast.setAttribute('role', 'alert');
    document.body.appendChild(toast);

    requestAnimationFrame(function () {
      toast.classList.add('is-visible');
    });

    setTimeout(function () {
      toast.classList.remove('is-visible');
      setTimeout(function () {
        toast.remove();
      }, 300);
    }, 3500);
  }

  function updateCartCount() {
    fetch('/cart.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        var badges = document.querySelectorAll('[data-cart-count]');
        badges.forEach(function (badge) {
          badge.textContent = cart.item_count > 0 ? cart.item_count : '';
          badge.setAttribute('data-count', cart.item_count);
        });
        updateShippingBar(cart.total_price);
      })
      .catch(function (err) {
        console.error('updateCartCount error:', err);
      });
  }

  /* ==========================================================================
     6. Cart Drawer
     ========================================================================== */

  function initCartDrawer() {
    var drawer = document.querySelector('.cart-drawer');
    var overlay = document.querySelector('.cart-drawer__overlay');
    var closeBtn = document.querySelector('.cart-drawer__close');

    if (!drawer) return;

    // Cart link clicks navigate to /cart page naturally (no interception).
    // The drawer is only opened programmatically after AJAX add-to-cart.

    if (closeBtn) {
      closeBtn.addEventListener('click', closeCartDrawer);
    }

    if (overlay) {
      overlay.addEventListener('click', closeCartDrawer);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('cart-drawer--open')) {
        closeCartDrawer();
      }
    });
  }

  function openCartDrawer() {
    var drawer = document.querySelector('.cart-drawer');
    var overlay = document.querySelector('.cart-drawer__overlay');
    if (!drawer) {
      window.location.href = '/cart';
      return;
    }

    // Show loading spinner immediately so drawer isn't a blank white screen
    var container = drawer.querySelector('.cart-drawer__items');
    if (container) {
      container.innerHTML = '<div class="cart-drawer__loading"><div class="cart-drawer__spinner"></div><p>Loading cart&hellip;</p></div>';
    }

    drawer.classList.add('cart-drawer--open');
    if (overlay) overlay.classList.add('cart-drawer__overlay--visible');
    lockBodyScroll();

    fetchAndRenderCart();
    trapFocus(drawer);
  }

  // Expose cart functions globally for inline handlers
  window.MR = window.MR || {};
  window.MR.openCart = openCartDrawer;
  window.MR.closeCart = closeCartDrawer;

  function closeCartDrawer() {
    var drawer = document.querySelector('.cart-drawer');
    var overlay = document.querySelector('.cart-drawer__overlay');
    if (!drawer) return;

    drawer.classList.remove('cart-drawer--open');
    if (overlay) overlay.classList.remove('cart-drawer__overlay--visible');
    unlockBodyScroll();
  }

  function fetchAndRenderCart() {
    var container = document.querySelector('.cart-drawer__items');
    var subtotalEl = document.querySelector('.cart-drawer__subtotal-price');
    var checkoutBtn = document.querySelector('.cart-drawer__checkout');
    var countEl = document.querySelector('.cart-drawer__count');

    fetch('/cart.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Cart fetch failed: ' + r.status);
        return r.json();
      })
      .then(function (cart) {
        if (!container) return;

        if (countEl) countEl.textContent = cart.item_count;

        if (cart.items.length === 0) {
          container.innerHTML = '<div class="cart-drawer__empty"><p>Your cart is empty</p></div>';
          if (checkoutBtn) {
            checkoutBtn.setAttribute('aria-disabled', 'true');
            checkoutBtn.style.pointerEvents = 'none';
            checkoutBtn.style.opacity = '0.6';
          }
          if (subtotalEl) subtotalEl.textContent = formatMoney(0);
          updateShippingBar(0);
          return;
        }

        if (checkoutBtn) {
          checkoutBtn.removeAttribute('aria-disabled');
          checkoutBtn.style.pointerEvents = '';
          checkoutBtn.style.opacity = '';
        }

        var html = '';
        cart.items.forEach(function (item, index) {
          var imageUrl = escapeHtml(item.image || (item.featured_image ? item.featured_image.url : '') || '');
          var variantTitle = escapeHtml(item.variant_title || '');
          var productTitle = escapeHtml(item.product_title || '');
          var itemUrl = escapeHtml(item.url || '');
          var itemKey = escapeHtml(item.key || '');
          var itemTitle = escapeHtml(item.title || '');
          var lineIndex = index + 1;
          var safeQty = parseInt(item.quantity, 10) || 1;
          html += '<div class="cart-drawer-item" data-key="' + itemKey + '" data-line="' + lineIndex + '">';
          html += '  <a href="' + itemUrl + '" class="cart-drawer-item__image">';
          if (item.image || (item.featured_image && item.featured_image.url)) {
            html += '    <img src="' + imageUrl + '" alt="' + itemTitle + '" width="60" height="60" loading="lazy">';
          }
          html += '  </a>';
          html += '  <div class="cart-drawer-item__info">';
          html += '    <a href="' + itemUrl + '" class="cart-drawer-item__title">' + productTitle + '</a>';
          if (variantTitle) {
            html += '    <p class="cart-drawer-item__variant">' + variantTitle + '</p>';
          }
          html += '    <div class="cart-drawer-item__bottom">';
          html += '      <div class="quantity quantity--sm">';
          html += '        <button class="quantity__btn quantity__btn--minus" type="button" data-action="decrease" data-key="' + itemKey + '" aria-label="Decrease quantity">-</button>';
          html += '        <input type="number" value="' + safeQty + '" min="1" max="999" class="quantity__input" data-key="' + itemKey + '" aria-label="Quantity">';
          html += '        <button class="quantity__btn quantity__btn--plus" type="button" data-action="increase" data-key="' + itemKey + '" aria-label="Increase quantity">+</button>';
          html += '      </div>';
          html += '      <span class="cart-drawer-item__price">' + formatMoney(item.final_line_price) + '</span>';
          html += '    </div>';
          html += '  </div>';
          html += '  <button class="cart-drawer-item__remove" data-key="' + itemKey + '" aria-label="Remove ' + productTitle + '">';
          html += '    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
          html += '  </button>';
          html += '</div>';
        });

        container.innerHTML = html;

        if (subtotalEl) {
          subtotalEl.textContent = formatMoney(cart.total_price);
        }

        updateShippingBar(cart.total_price);

        // Update cart count badges
        var badges = document.querySelectorAll('[data-cart-count]');
        badges.forEach(function (badge) {
          badge.textContent = cart.item_count > 0 ? cart.item_count : '';
          badge.setAttribute('data-count', cart.item_count);
        });
      })
      .catch(function (err) {
        console.error('fetchAndRenderCart error:', err);
        if (container) {
          container.innerHTML = '<p style="padding:1rem;color:#666;">Unable to load cart. <a href="/cart">View cart page</a></p>';
        }
      });
  }

  function initCartDrawerEvents() {
    document.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('.quantity__btn');
      if (qtyBtn && qtyBtn.closest('.cart-drawer')) {
        var key = qtyBtn.getAttribute('data-key');
        var action = qtyBtn.classList.contains('quantity__btn--plus') ? 'increase' : 'decrease';
        var inputEl = qtyBtn.parentElement.querySelector('.quantity__input');
        var currentQty = parseInt(inputEl.value, 10) || 1;
        var newQty = action === 'increase' ? currentQty + 1 : currentQty - 1;

        if (newQty < 1) newQty = 0;
        if (newQty > 999) newQty = 999;
        changeCartItem(key, newQty);
        return;
      }

      var removeBtn = e.target.closest('.cart-drawer-item__remove');
      if (removeBtn) {
        var removeKey = removeBtn.getAttribute('data-key');
        changeCartItem(removeKey, 0);
      }
    });
  }

  function changeCartItem(key, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart change failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        fetchAndRenderCart();
        updateCartCount();
      })
      .catch(function (err) {
        console.error('changeCartItem error:', err);
        showCartError('Failed to update cart. Please try again.');
      });
  }

  /* ==========================================================================
     7. Product Variant Selector
     ========================================================================== */

  function initVariantSelector() {
    var productData = document.querySelector('[data-product-json]');
    if (!productData) return;

    var product;
    try {
      product = JSON.parse(productData.textContent);
    } catch (e) {
      return;
    }

    var variantSelectors = document.querySelectorAll('[data-option-radio], [data-option-select]');
    if (variantSelectors.length === 0) return;

    function getSelectedOptions() {
      var options = [];
      var optionGroups = document.querySelectorAll('.product__option');
      optionGroups.forEach(function (group) {
        var selected = group.querySelector('input[type="radio"]:checked');
        if (selected) {
          options.push(selected.value);
        } else {
          var selectEl = group.querySelector('select');
          if (selectEl) {
            options.push(selectEl.value);
          }
        }
      });
      return options;
    }

    function findVariant(selectedOptions) {
      return product.variants.find(function (variant) {
        return variant.options.every(function (opt, index) {
          return opt === selectedOptions[index];
        });
      });
    }

    function updateProductPage(variant) {
      var priceEl = document.querySelector('.product__price-wrapper .price__sale, .product__price-wrapper .price__regular');
      var comparePriceEl = document.querySelector('.product__price-wrapper .price__compare');
      var skuEl = document.querySelector('[data-sku-value]');
      var availabilityEl = document.querySelector('[data-product-availability]');
      var variantInput = document.querySelector('[data-variant-id]');
      var addToCartBtn = document.querySelector('[data-add-to-cart]');
      var mainImage = document.querySelector('#product-featured-image');

      if (!variant) {
        if (addToCartBtn) {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'Unavailable';
        }
        if (availabilityEl) {
          availabilityEl.textContent = 'Unavailable';
          availabilityEl.classList.add('is-unavailable');
        }
        return;
      }

      if (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
      }

      if (comparePriceEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          comparePriceEl.textContent = formatMoney(variant.compare_at_price);
          comparePriceEl.style.display = '';
        } else {
          comparePriceEl.textContent = '';
          comparePriceEl.style.display = 'none';
        }
      }

      if (skuEl) {
        skuEl.textContent = variant.sku || '';
      }

      if (variantInput) {
        variantInput.value = variant.id;
      }

      if (variant.available) {
        if (addToCartBtn) {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = 'Add to Cart';
        }
        if (availabilityEl) {
          availabilityEl.textContent = 'In stock';
          availabilityEl.classList.remove('is-unavailable');
          availabilityEl.classList.add('is-available');
        }
      } else {
        if (addToCartBtn) {
          addToCartBtn.disabled = true;
          addToCartBtn.textContent = 'Sold Out';
        }
        if (availabilityEl) {
          availabilityEl.textContent = 'Sold out';
          availabilityEl.classList.add('is-unavailable');
          availabilityEl.classList.remove('is-available');
        }
      }

      // Update product image if variant has featured image
      if (variant.featured_image && mainImage) {
        mainImage.src = variant.featured_image.src;
        mainImage.alt = variant.featured_image.alt || variant.title;
      }

      // Update URL without reload
      if (variant.id) {
        var newUrl = window.location.pathname + '?variant=' + variant.id;
        window.history.replaceState({ variant: variant.id }, '', newUrl);
      }

      // Dispatch variant:change event for other components (back-in-stock, etc.)
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: variant,
          productTitle: product.title,
          productHandle: product.handle
        }
      }));
    }

    variantSelectors.forEach(function (selector) {
      var eventType = selector.tagName === 'SELECT' ? 'change' : 'change';
      selector.addEventListener(eventType, function () {
        var selectedOptions = getSelectedOptions();
        var variant = findVariant(selectedOptions);
        updateProductPage(variant);
      });
    });
  }

  /* ==========================================================================
     8. Product Image Gallery
     ========================================================================== */

  function initProductGallery() {
    var mainImage = document.querySelector('#product-featured-image');
    var thumbnails = document.querySelectorAll('[data-thumbnail]');
    var galleryContainer = document.querySelector('.product__gallery');

    if (!mainImage) return;

    // Thumbnail click
    thumbnails.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var newSrc = thumb.getAttribute('data-image-src');
        var newSrcset = thumb.getAttribute('data-image-srcset');
        var newAlt = thumb.getAttribute('data-image-alt') || '';
        var newWidth = thumb.getAttribute('data-image-width');
        var newHeight = thumb.getAttribute('data-image-height');

        if (newSrc) mainImage.src = newSrc;
        if (newSrcset) mainImage.srcset = newSrcset;
        if (newAlt) mainImage.alt = newAlt;
        if (newWidth) mainImage.width = newWidth;
        if (newHeight) mainImage.height = newHeight;

        // Active thumbnail highlight
        thumbnails.forEach(function (t) {
          t.classList.remove('product__thumbnail--active');
        });
        thumb.classList.add('product__thumbnail--active');
      });
    });

    // Image zoom on hover (desktop)
    if (galleryContainer && window.matchMedia('(pointer: fine)').matches) {
      var mainImageWrap = mainImage.closest('.product__gallery-main');
      if (mainImageWrap) {
        mainImageWrap.addEventListener('mouseenter', function () {
          mainImage.style.transition = 'transform 0.1s ease';
          mainImage.style.transform = 'scale(1.5)';
        });

        mainImageWrap.addEventListener('mousemove', function (e) {
          var rect = mainImageWrap.getBoundingClientRect();
          var x = ((e.clientX - rect.left) / rect.width) * 100;
          var y = ((e.clientY - rect.top) / rect.height) * 100;
          mainImage.style.transformOrigin = x + '% ' + y + '%';
        });

        mainImageWrap.addEventListener('mouseleave', function () {
          mainImage.style.transform = 'scale(1)';
          mainImage.style.transformOrigin = 'center center';
        });
      }
    }

    // Swipe support on mobile (touch events)
    var touchStartX = 0;
    var touchEndX = 0;
    var currentIndex = 0;

    if (galleryContainer) {
      galleryContainer.addEventListener('touchstart', function (e) {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      galleryContainer.addEventListener('touchend', function (e) {
        touchEndX = e.changedTouches[0].screenX;
        handleSwipe();
      }, { passive: true });
    }

    function handleSwipe() {
      var diff = touchStartX - touchEndX;
      var threshold = 50;

      if (thumbnails.length < 2) return;

      // Find current active index
      thumbnails.forEach(function (t, i) {
        if (t.classList.contains('product__thumbnail--active')) {
          currentIndex = i;
        }
      });

      if (diff > threshold) {
        // Swipe left -> next image
        currentIndex = (currentIndex + 1) % thumbnails.length;
      } else if (diff < -threshold) {
        // Swipe right -> prev image
        currentIndex = (currentIndex - 1 + thumbnails.length) % thumbnails.length;
      } else {
        return;
      }

      thumbnails[currentIndex].click();
    }
  }

  /* ==========================================================================
     9. Product Tabs
     ========================================================================== */

  function initProductTabs() {
    var tabContainers = document.querySelectorAll('.product__tabs');

    tabContainers.forEach(function (container) {
      var tabBtns = container.querySelectorAll('[data-tab-btn]');
      var tabPanels = container.querySelectorAll('[data-tab-panel]');

      if (tabBtns.length === 0 || tabPanels.length === 0) return;

      tabBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          var target = btn.getAttribute('data-tab-btn');

          // Deactivate all
          tabBtns.forEach(function (b) {
            b.classList.remove('product__tab--active');
            b.setAttribute('aria-selected', 'false');
          });
          tabPanels.forEach(function (p) {
            p.classList.remove('product__tab-panel--active');
            p.hidden = true;
          });

          // Activate clicked
          btn.classList.add('product__tab--active');
          btn.setAttribute('aria-selected', 'true');
          var targetPanel = container.querySelector('[data-tab-panel="' + target + '"]');
          if (targetPanel) {
            targetPanel.classList.add('product__tab-panel--active');
            targetPanel.hidden = false;
          }
        });
      });
    });
  }

  /* ==========================================================================
     10. Sticky Add-to-Cart Bar
     ========================================================================== */

  function initStickyAddBar() {
    var stickyBar = document.querySelector('[data-sticky-add-bar]');
    var mainAddBtn = document.querySelector('[data-add-to-cart]');

    if (!stickyBar || !mainAddBtn) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            stickyBar.classList.remove('sticky-add-bar--visible');
          } else {
            stickyBar.classList.add('sticky-add-bar--visible');
          }
        });
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px'
      }
    );

    observer.observe(mainAddBtn);

    // Sticky bar "Add to Cart" triggers the main form
    var stickyAddBtn = stickyBar.querySelector('[data-sticky-add-btn]');
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var mainForm = mainAddBtn.closest('form[action="/cart/add"]') || mainAddBtn.closest('form[action*="/cart/add"]');
        if (mainForm) {
          submitAddToCart(mainForm);
        }
      });
    }
  }

  /* ==========================================================================
     12. Newsletter Popup
     ========================================================================== */

  function initNewsletterPopup() {
    var overlay = document.querySelector('.newsletter-popup-overlay');
    if (!overlay) return;

    // Don't fire on cart, checkout, or sell-your-device pages
    var path = window.location.pathname;
    if (path.indexOf('/cart') === 0 || path.indexOf('/checkout') === 0 || path.indexOf('/pages/sell-your-device') === 0) return;

    if (localStorage.getItem('mr_popup_dismissed') === 'true') return;

    var closeBtn = overlay.querySelector('.newsletter-popup__close');
    var emailInput = overlay.querySelector('input[type="email"]');
    var pageHeight = document.documentElement.scrollHeight;
    var pageLoadTime = Date.now();
    var shown = false;
    var lastFocused = null;

    function showPopup() {
      if (shown) return;
      shown = true;

      // Listeners are no-ops once shown — detach to free the main thread
      document.removeEventListener('mouseleave', onMouseLeave);
      window.removeEventListener('scroll', onScroll);

      lastFocused = document.activeElement;
      overlay.classList.add('newsletter-popup-overlay--visible');
      overlay.style.display = '';
      lockBodyScroll();

      // Move focus into the dialog and trap Tab cycling
      if (emailInput) {
        try { emailInput.focus({ preventScroll: true }); } catch (_) { emailInput.focus(); }
      }
      trapFocus(overlay);
    }

    function hidePopup() {
      overlay.classList.remove('newsletter-popup-overlay--visible');
      unlockBodyScroll();
      localStorage.setItem('mr_popup_dismissed', 'true');

      // Restore focus to the element that had it before the popup opened
      if (lastFocused && typeof lastFocused.focus === 'function') {
        try { lastFocused.focus({ preventScroll: true }); } catch (_) { lastFocused.focus(); }
      }
    }

    // Trigger 1: exit-intent (mouse leaves top of viewport)
    function onMouseLeave(e) {
      if (e.clientY <= 0) showPopup();
    }
    document.addEventListener('mouseleave', onMouseLeave);

    // Trigger 2: scroll past 50% of page (fallback for touch devices),
    // but only after a 15-second grace window so fast mobile flicks
    // don't get interrupted before the user has read anything.
    function onScroll() {
      if (Date.now() - pageLoadTime < 15000) return;
      var scrolled = window.scrollY + window.innerHeight;
      if (pageHeight > 0 && scrolled / pageHeight >= 0.5) showPopup();
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    // Refresh cached pageHeight on resize (lazy-loaded images, font swaps)
    window.addEventListener('resize', function () {
      pageHeight = document.documentElement.scrollHeight;
    }, { passive: true });

    // Close button
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        hidePopup();
      });
    }

    // Close on overlay click (not inner popup)
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) hidePopup();
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('newsletter-popup-overlay--visible')) hidePopup();
    });
  }

  /* ==========================================================================
     13. Collection Filters
     ========================================================================== */

  function initCollectionFilters() {
    var filterToggle = document.querySelector('.collection-toolbar__filter-btn');
    var filterSidebar = document.querySelector('.collection-filters');
    var filterClose = document.querySelector('.collection-filters__close');

    // Toggle filter sidebar on mobile
    if (filterToggle && filterSidebar) {
      filterToggle.addEventListener('click', function () {
        filterSidebar.classList.toggle('is-open');
        filterToggle.classList.toggle('is-active');
        var isOpen = filterSidebar.classList.contains('is-open');
        filterToggle.setAttribute('aria-expanded', String(isOpen));
      });
    }

    if (filterClose && filterSidebar) {
      filterClose.addEventListener('click', function () {
        filterSidebar.classList.remove('is-open');
        if (filterToggle) filterToggle.classList.remove('is-active');
      });
    }

    // Collapsible filter groups
    var filterToggles = document.querySelectorAll('.filter-group__toggle');
    filterToggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var group = toggle.closest('.filter-group');
        if (!group) return;
        var content = group.querySelector('.filter-group__content');
        if (!content) return;

        var isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isExpanded));

        if (isExpanded) {
          content.style.display = 'none';
        } else {
          content.style.display = '';
        }
      });
    });

    // Form submit on filter change
    var filterForm = document.querySelector('#filter-form');
    if (filterForm) {
      filterForm.addEventListener('change', function () {
        filterForm.submit();
      });
    }
  }

  /* ==========================================================================
     14. Quantity Selectors
     ========================================================================== */

  function initQuantitySelectors() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.quantity__btn');
      if (!btn) return;

      var wrapper = btn.closest('.product__quantity-selector') || btn.closest('.quantity');
      if (!wrapper) return;

      // Skip if this is a cart drawer quantity (handled separately)
      if (btn.closest('.cart-drawer')) return;

      var input = wrapper.querySelector('input[type="number"]');
      if (!input) return;

      var currentVal = parseInt(input.value, 10) || 1;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10) || 99;
      var isMinus = btn.classList.contains('product__quantity-btn--minus') || btn.classList.contains('quantity__btn--minus') || btn.getAttribute('data-action') === 'decrease' || btn.hasAttribute('data-quantity-minus');
      var action = isMinus ? 'decrease' : 'increase';

      if (action === 'increase') {
        if (currentVal < max) {
          input.value = currentVal + 1;
        }
      } else if (action === 'decrease') {
        if (currentVal > min) {
          input.value = currentVal - 1;
        }
      }

      // Dispatch change event for any listeners
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    // Prevent manual input below min or above max
    document.addEventListener('change', function (e) {
      if (!e.target.matches('.quantity__input')) return;
      var input = e.target;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10) || 99;
      var val = parseInt(input.value, 10);

      if (isNaN(val) || val < min) {
        input.value = min;
      } else if (val > max) {
        input.value = max;
      }
    });
  }

  /* ==========================================================================
     14b. Cart Page — AJAX quantity, remove, and clear
     ========================================================================== */

  function initCartPage() {
    var cartForm = document.getElementById('cart-form');
    if (!cartForm) return;

    // AJAX quantity change on +/- click (cart page)
    cartForm.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('.quantity__btn');
      if (!qtyBtn) return;

      var cartItem = qtyBtn.closest('.cart-item');
      if (!cartItem) return;

      var input = cartItem.querySelector('.quantity__input');
      if (!input) return;

      var line = parseInt(cartItem.getAttribute('data-line'), 10);
      var newQty = parseInt(input.value, 10) || 1;

      // The initQuantitySelectors handler already updated input.value,
      // so read the new value after a tick
      setTimeout(function () {
        newQty = parseInt(input.value, 10);
        if (isNaN(newQty) || newQty < 0) newQty = 0;
        cartPageUpdateLine(line, newQty);
      }, 0);
    });

    // AJAX remove button
    cartForm.addEventListener('click', function (e) {
      var removeBtn = e.target.closest('.cart-item__remove');
      if (!removeBtn) return;
      e.preventDefault();

      var cartItem = removeBtn.closest('.cart-item');
      if (!cartItem) return;

      var line = parseInt(cartItem.getAttribute('data-line'), 10);
      cartItem.style.opacity = '0.4';
      cartPageUpdateLine(line, 0);
    });

    // Clear cart button
    var clearBtn = document.querySelector('.cart-page__clear');
    if (clearBtn) {
      clearBtn.addEventListener('click', function (e) {
        e.preventDefault();
        clearBtn.textContent = 'Clearing...';
        clearBtn.disabled = true;
        fetch('/cart/clear.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        })
          .then(function (r) {
            if (!r.ok) throw new Error('Clear failed');
            window.location.reload();
          })
          .catch(function () {
            window.location.href = '/cart';
          });
      });
    }
  }

  function cartPageUpdateLine(line, quantity) {
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('Cart update failed');
        // Reload page to reflect changes (simplest approach)
        window.location.reload();
      })
      .catch(function () {
        window.location.reload();
      });
  }

  /* ==========================================================================
     15. Free Shipping Bar Update
     ========================================================================== */

  function updateShippingBar(cartTotalCents) {
    var bar = document.querySelector('.free-shipping-bar');
    if (!bar) return;

    var progressFill = bar.querySelector('.free-shipping-bar__fill');
    var messageEl = bar.querySelector('[data-shipping-text]');
    if (!progressFill) return;

    var drawer = document.querySelector('.cart-drawer');
    var thresholdAttr = drawer ? drawer.getAttribute('data-shipping-threshold') : null;
    var thresholdCents = thresholdAttr ? parseInt(thresholdAttr, 10) * 100 : 10000;

    if (cartTotalCents >= thresholdCents) {
      progressFill.style.width = '100%';
      if (messageEl) messageEl.textContent = 'You qualify for free shipping!';
      bar.classList.add('free-shipping-bar--achieved');
    } else {
      var remaining = thresholdCents - cartTotalCents;
      var percentage = Math.min((cartTotalCents / thresholdCents) * 100, 100);
      progressFill.style.width = percentage + '%';
      if (messageEl) messageEl.textContent = 'You are ' + formatMoney(remaining) + ' away from free shipping!';
      bar.classList.remove('free-shipping-bar--achieved');
    }
  }

  /* ==========================================================================
     16. Scroll-to-Top
     ========================================================================== */

  function initScrollToTop() {
    var btn = document.querySelector('.scroll-to-top');
    if (!btn) return;

    var scrollThreshold = 600;

    window.addEventListener('scroll', function () {
      if (window.scrollY > scrollThreshold) {
        btn.classList.add('is-visible');
      } else {
        btn.classList.remove('is-visible');
      }
    }, { passive: true });

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    });
  }

  /* ==========================================================================
     17. Lazy Loading Enhancement
     ========================================================================== */

  function initLazyLoading() {
    // Only run fallback if native lazy loading is not supported
    if ('loading' in HTMLImageElement.prototype) return;

    var lazyImages = document.querySelectorAll('img[loading="lazy"]');
    if (lazyImages.length === 0) return;

    var imageObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var img = entry.target;
          if (img.dataset.src) {
            img.src = img.dataset.src;
          }
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
          }
          img.classList.add('is-loaded');
          imageObserver.unobserve(img);
        }
      });
    }, {
      rootMargin: '200px 0px'
    });

    lazyImages.forEach(function (img) {
      imageObserver.observe(img);
    });
  }

  /* ==========================================================================
     Back-in-Stock Notification Toggle
     ========================================================================== */

  function initBackInStock() {
    var trigger = document.querySelector('[data-bis-trigger]');
    var form = document.querySelector('[data-bis-form]');
    if (!trigger || !form) return;

    trigger.addEventListener('click', function () {
      trigger.style.display = 'none';
      form.style.display = '';
      var input = form.querySelector('input[type="email"]');
      if (input) input.focus();
    });

    /* Update back-in-stock visibility when variant changes */
    document.addEventListener('variant:change', function (e) {
      var bis = document.querySelector('[data-back-in-stock]');
      if (!bis) return;
      var variant = e.detail && e.detail.variant;
      if (variant && variant.available) {
        bis.style.display = 'none';
      } else {
        bis.style.display = '';
        trigger.style.display = '';
        form.style.display = 'none';
      }
      /* Update the hidden note field with new variant info */
      var note = bis.querySelector('[data-bis-note]');
      if (note && variant) {
        note.value = 'Back in stock request: ' + (e.detail.productTitle || '') + ' | ' + (e.detail.productHandle || '') + ' | Variant: ' + (variant.title || '') + ' | ID: ' + variant.id;
      }
    });
  }

  /* ==========================================================================
     N. Brand Slider Pause Toggle (WCAG 2.2.2)
     ========================================================================== */

  function initBrandSliderPause() {
    var btn = document.querySelector('.brand-slider__pause-toggle');
    var track = document.querySelector('.brand-slider__track');
    if (!btn || !track) return;

    var STORAGE_KEY = 'mr_brand_slider_paused';

    function setPaused(paused) {
      if (paused) {
        track.classList.add('brand-slider__track--paused');
        btn.setAttribute('aria-pressed', 'true');
        btn.setAttribute('aria-label', 'Resume brand slider');
        try { localStorage.setItem(STORAGE_KEY, 'true'); } catch (_) {}
      } else {
        track.classList.remove('brand-slider__track--paused');
        btn.setAttribute('aria-pressed', 'false');
        btn.setAttribute('aria-label', 'Pause brand slider');
        try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
      }
    }

    // Restore prior preference
    try {
      if (localStorage.getItem(STORAGE_KEY) === 'true') setPaused(true);
    } catch (_) {}

    btn.addEventListener('click', function () {
      var currentlyPaused = btn.getAttribute('aria-pressed') === 'true';
      setPaused(!currentlyPaused);
    });
  }

  /* ==========================================================================
     Initialize All Modules
     ========================================================================== */

  function safeInit(name, fn) {
    try { fn(); } catch (e) { console.error(name + ' init failed:', e); }
  }

  function initAll() {
    safeInit('MobileMenu', initMobileMenu);
    safeInit('MegaMenu', initMegaMenu);
    safeInit('AnnouncementBar', initAnnouncementBar);
    safeInit('PredictiveSearch', initPredictiveSearch);
    safeInit('AjaxCart', initAjaxCart);
    safeInit('CartDrawer', initCartDrawer);
    safeInit('CartDrawerEvents', initCartDrawerEvents);
    safeInit('VariantSelector', initVariantSelector);
    safeInit('ProductGallery', initProductGallery);
    safeInit('ProductTabs', initProductTabs);
    safeInit('StickyAddBar', initStickyAddBar);
    safeInit('NewsletterPopup', initNewsletterPopup);
    safeInit('CollectionFilters', initCollectionFilters);
    safeInit('QuantitySelectors', initQuantitySelectors);
    safeInit('CartPage', initCartPage);
    safeInit('ScrollToTop', initScrollToTop);
    safeInit('LazyLoading', initLazyLoading);
    safeInit('BackInStock', initBackInStock);
    safeInit('CategoryFilters', initCategoryFilters);
    safeInit('BrandSliderPause', initBrandSliderPause);
  }

  /* ==========================================================================
     Category Filters (title-based, auto-generated)
     ========================================================================== */

  function initCategoryFilters() {
    var container = document.getElementById('category-filters');
    if (!container) return;

    var grid = document.querySelector('.collection-grid') || document.querySelector('.search-results');
    if (!grid) { container.style.display = 'none'; return; }
    var cards = Array.from(grid.querySelectorAll('.product-card'));
    if (cards.length === 0) { container.style.display = 'none'; return; }

    var filters = { brand: {}, device: {}, part: {} };
    var activeFilters = { brand: null, device: null, part: null };

    /* Scan cards for data attributes */
    cards.forEach(function (card) {
      var brand = card.getAttribute('data-brand') || '';
      var device = card.getAttribute('data-device') || '';
      var part = card.getAttribute('data-part') || '';
      if (brand) filters.brand[brand] = (filters.brand[brand] || 0) + 1;
      if (device) filters.device[device] = (filters.device[device] || 0) + 1;
      if (part) filters.part[part] = (filters.part[part] || 0) + 1;
    });

    /* Build chips for each filter group */
    ['brand', 'device', 'part'].forEach(function (key) {
      var chipContainer = document.getElementById('filter-chips-' + key);
      var group = container.querySelector('[data-filter="' + key + '"]');
      if (!chipContainer || !group) return;

      var entries = Object.entries(filters[key]).sort(function (a, b) { return b[1] - a[1]; });
      if (entries.length <= 1) { group.style.display = 'none'; return; }

      entries.forEach(function (entry) {
        var value = entry[0];
        var count = entry[1];
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'category-chip';
        chip.setAttribute('data-filter-key', key);
        chip.setAttribute('data-filter-value', value);
        chip.textContent = value + ' (' + count + ')';
        chip.addEventListener('click', function () {
          if (activeFilters[key] === value) {
            activeFilters[key] = null;
            chip.classList.remove('category-chip--active');
          } else {
            var siblings = chipContainer.querySelectorAll('.category-chip');
            siblings.forEach(function (s) { s.classList.remove('category-chip--active'); });
            activeFilters[key] = value;
            chip.classList.add('category-chip--active');
          }
          applyFilters();
        });
        chipContainer.appendChild(chip);
      });
    });

    /* Show/hide "Clear Filters" */
    var clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'category-filters__clear';
    clearBtn.textContent = 'Clear All Filters';
    clearBtn.style.display = 'none';
    clearBtn.addEventListener('click', function () {
      activeFilters.brand = null;
      activeFilters.device = null;
      activeFilters.part = null;
      container.querySelectorAll('.category-chip--active').forEach(function (c) {
        c.classList.remove('category-chip--active');
      });
      applyFilters();
    });
    container.appendChild(clearBtn);

    /* Auto-activate filters from URL params (e.g. &device=Xbox) */
    var urlParams = new URLSearchParams(window.location.search);
    ['brand', 'device', 'part'].forEach(function (key) {
      var paramValue = urlParams.get(key);
      if (paramValue) {
        var safeValue = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(paramValue) : paramValue.replace(/["\\]/g, '\\$&');
        var chip = container.querySelector('[data-filter-key="' + key + '"][data-filter-value="' + safeValue + '"]');
        if (chip) {
          activeFilters[key] = paramValue;
          chip.classList.add('category-chip--active');
        }
      }
    });
    applyFilters();

    /* Sort dropdown — preserves all current URL filter params */
    var sortSelect = document.getElementById('sort-select');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var anyChipActive = activeFilters.brand || activeFilters.device || activeFilters.part;
        var sortKey = sortSelect.value;

        if (anyChipActive) {
          /* Category chip filters active — sort DOM elements client-side */
          var sorted = cards.slice();
          sorted.sort(function (a, b) {
            switch (sortKey) {
              case 'title-ascending':
                return (a.getAttribute('data-title') || '').localeCompare(b.getAttribute('data-title') || '');
              case 'title-descending':
                return (b.getAttribute('data-title') || '').localeCompare(a.getAttribute('data-title') || '');
              case 'price-ascending':
                return (parseInt(a.getAttribute('data-price')) || 0) - (parseInt(b.getAttribute('data-price')) || 0);
              case 'price-descending':
                return (parseInt(b.getAttribute('data-price')) || 0) - (parseInt(a.getAttribute('data-price')) || 0);
              case 'created-descending':
                return (parseInt(b.getAttribute('data-created')) || 0) - (parseInt(a.getAttribute('data-created')) || 0);
              case 'created-ascending':
                return (parseInt(a.getAttribute('data-created')) || 0) - (parseInt(b.getAttribute('data-created')) || 0);
              default:
                return 0;
            }
          });
          sorted.forEach(function (card) { grid.appendChild(card); });
          applyFilters();
        } else {
          /* No chip filters — server-side sort, preserve all URL filter params */
          var params = new URLSearchParams(window.location.search);
          params.set('sort_by', sortKey);
          var baseUrl = sortSelect.getAttribute('data-collection-url') || window.location.pathname;
          window.location.href = baseUrl + '?' + params.toString();
        }
      });
    }

    function applyFilters() {
      var anyActive = activeFilters.brand || activeFilters.device || activeFilters.part;
      clearBtn.style.display = anyActive ? '' : 'none';

      var visibleCount = 0;
      cards.forEach(function (card) {
        var show = true;
        if (activeFilters.brand && card.getAttribute('data-brand') !== activeFilters.brand) show = false;
        if (activeFilters.device && card.getAttribute('data-device') !== activeFilters.device) show = false;
        if (activeFilters.part && card.getAttribute('data-part') !== activeFilters.part) show = false;
        card.style.display = show ? '' : 'none';
        if (show) visibleCount++;
      });

      /* Update count display */
      var countEl = document.querySelector('.collection-toolbar__count');
      if (countEl) {
        if (anyActive) {
          countEl.textContent = visibleCount + ' of ' + cards.length + ' products';
        } else {
          countEl.textContent = cards.length + ' products';
        }
      }
    }
  }

  // Handle both normal and late-loading scenarios
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

})();
